<template>
  <StockImportEntry v-if="stockBootHandled" />
  <EditorEntry
    v-else
    :auto-navigate-to-editor="autoNavigateToEditor ?? false"
    @category:selected="onCategory"
    @product:selected="onProduct"
  />
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { useRoute, useRouter } from 'vue-router'
import EditorEntry from '../editor/entry.vue'
import StockImportEntry from './stock-import.vue'
import { useEditorConfigStore } from '~/store/useEditorConfigStore'
import { useEditorNav } from '~/composables/useEditorNav'
import { getProduct } from '~/gql/product/query'

/**
 * Route-Props werden vom Router injiziert (aus ?category=X&product=Y etc.)
 * Zusätzlich kann der Widget-Host categoryId + productId als Props übergeben.
 */
const props = defineProps<{
  parentCategoryId?: string | null
  categoryId?: number | null
  productId?: number | null
  autoNavigateToEditor?: boolean
}>()

const route = useRoute()
const router = useRouter()
const editorConfig = useEditorConfigStore()
const { goToEditor } = useEditorNav()
const stockBootHandled = computed(() => {
  const raw = route.query.assetId ?? route.query.stockAssetId
  const assetId = String(Array.isArray(raw) ? raw[0] : raw || '').trim()
  const continueToEditor = String(Array.isArray(route.query.stockContinue) ? route.query.stockContinue[0] : route.query.stockContinue || '').trim() === '1'
  return continueToEditor ? '' : assetId
})

// ─── Auto-Navigate: Wenn beide IDs via Props bekannt sind (z.B. Embed oder ?auto=1) ──
const autoProductIri = computed(() =>
  props.productId && props.categoryId ? `/api/products/${props.productId}` : null
)

const { result: autoProductData } = useQuery(
  getProduct,
  computed(() => ({ id: autoProductIri.value })),
  () => ({
    enabled: !!autoProductIri.value && props.autoNavigateToEditor === true,
    fetchPolicy: 'network-only',
  })
)

watch(
  autoProductData,
  (data) => {
    const product = data?.product
    if (!product || !props.productId || !props.categoryId) return

    const edges: any[] = product?.categories?.edges ?? []
    const hit = edges.map((e: any) => e?.node).find((c: any) => Number(c?._id) === Number(props.categoryId))
    const editorType = hit?.editorType === 'multi' ? 'multi' : 'single'

    goToEditor({
      type: editorType,
      categoryId: props.categoryId,
      productId: props.productId,
    })
  },
  { once: true },
)

watch(
  () => stockBootHandled.value,
  async (assetId) => {
    if (!assetId) return
    if (route.path === '/stock-import') return
    await router.replace({
      path: '/stock-import',
      query: { ...route.query },
    }).catch(() => {})
  },
  { immediate: true },
)

// ─── Handlers ────────────────────────────────────────────────────────────────
function onCategory(payload: { categoryId: number; title: string }) {
  editorConfig.setCategory({ id: payload.categoryId, title: payload.title })
}

function onProduct(payload: { productId: number; title: string }) {
  editorConfig.setProduct({ id: payload.productId, title: payload.title })
}
</script>
